import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { imageUrl } from '../config/images';
import PosterPlaceholder from './PosterPlaceholder';

// Presentational horizontal carousel of movie/TV posters. Pure render — the
// caller supplies items + loading state. Extracted from Home.js so the same
// markup powers both the default landing carousels and the (upcoming)
// user-configurable dashboard.
export default function CarouselSection({ title, note, items = [], isLoading, viewAllHref }) {
  return (
    <div className="bg-white py-10 px-4 border-t border-gray-100">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-baseline gap-3 mb-4">
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          {note && <span className="text-xs text-gray-400 hidden sm:block truncate">{note}</span>}
          {viewAllHref && items.length > 0 && (
            <Link to={viewAllHref}
                  className="ml-auto flex items-center gap-0.5 text-sm font-medium text-videbits-600 hover:text-videbits-700">
              View all <ChevronRight className="w-4 h-4" />
            </Link>
          )}
        </div>
        {isLoading ? (
          <div className="flex gap-3 overflow-hidden">
            {Array.from({ length: 10 }).map((_, i) => (
              <div key={i} className="flex-shrink-0 w-28 h-44 rounded-xl bg-gray-100 animate-pulse" />
            ))}
          </div>
        ) : items.length > 0 && (
          <div
            className="flex gap-3 overflow-x-auto pb-2"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {items.map((item) => {
              const poster = item.poster_path
                ? imageUrl(item.poster_path, 'w200')
                : null;
              return (
                <Link
                  key={item.id}
                  to={item.category === 'TV Series' ? `/tv/${item.id}` : `/movie/${item.id}`}
                  onClick={() => sessionStorage.setItem(`movie_${item.id}`, JSON.stringify(item))}
                  className="flex-shrink-0 w-28 group"
                >
                  <div className="w-28 h-40 rounded-xl overflow-hidden bg-gray-100 mb-2 shadow-sm group-hover:shadow-md transition-all duration-200">
                    {poster ? (
                      <img
                        src={poster}
                        alt={item.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                      />
                    ) : (
                      <PosterPlaceholder />
                    )}
                  </div>
                  <p className="text-xs font-medium text-gray-800 line-clamp-2 leading-tight">{item.title}</p>
                  {item.vote_average > 0 && (
                    <p className="text-xs text-gray-400 mt-0.5">★ {item.vote_average.toFixed(1)}</p>
                  )}
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
